<script setup lang="ts">
// "About this screen" (H-167): a button beside the page's name opens what the screen is for and who can do what there,
// the reader's own row marked. A popover by click, tap or keyboard (no hover-only tooltip: phones have no hover); Esc,
// a click elsewhere, a new page or a resize closes it, and Esc gives focus back to the button.
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import Icon from "@/components/ui/Icon.vue";
import { useAuthStore } from "@/stores/auth";
import { useWorkspaceStore } from "@/stores/workspace";
import { readerIs, screenHelp, screenKey, WHO, whoSays } from "@/lib/screens";
import { topicsFor } from "@/features/guide/topics";

const route = useRoute();
const auth = useAuthStore();
const workspace = useWorkspaceStore();
const help = computed(() => screenHelp(route));
// the guide's walkthroughs that use this screen (H-167 slice 2)
const guides = computed(() => topicsFor(screenKey(route) ?? ""));
// the role the server returns counts; the token's claim is only a first guess (stores/workspace.ts)
const reader = computed(() => readerIs(workspace.me?.role ?? auth.role, workspace.workspaces, workspace.current));

const open = ref(false);
const button = ref<HTMLButtonElement | null>(null);
const card = ref<HTMLElement | null>(null);
const pos = ref({ top: 0, left: 0 });

async function toggle() {
  if (open.value) return close();
  const r = button.value?.getBoundingClientRect();
  if (r) pos.value = { top: Math.round(r.bottom + 8), left: Math.round(Math.min(r.left, window.innerWidth - 392)) };
  open.value = true;
  await nextTick();
  card.value?.focus();
}
function close(refocus = false) {
  open.value = false;
  if (refocus) button.value?.focus();
}
const away = (e: Event) => {
  const t = e.target as Node | null;
  if (open.value && t && !button.value?.contains(t) && !card.value?.contains(t)) close();
};
const onResize = () => close();
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", away, true);
  window.addEventListener("resize", onResize);
}
onBeforeUnmount(() => {
  window.removeEventListener("pointerdown", away, true);
  window.removeEventListener("resize", onResize);
});
watch(() => route.fullPath, () => close());
</script>

<template>
  <span v-if="help" class="screen-help">
    <button ref="button" type="button" class="icon-btn help-btn" aria-label="About this screen" title="About this screen"
            :aria-expanded="open" aria-controls="screen-help" @click="toggle">
      <Icon name="info" :size="16" />
    </button>
    <Teleport to="body">
      <section v-if="open" id="screen-help" ref="card" class="help-card" role="dialog" aria-labelledby="screen-help-title"
               tabindex="-1" :style="{ top: `${pos.top}px`, left: `${pos.left}px` }" @keydown.esc.stop="close(true)">
        <p class="eyebrow">About this screen</p>
        <h2 id="screen-help-title">{{ help.title }}</h2>
        <p class="purpose">{{ help.purpose }}</p>
        <h3>Who can do what here</h3>
        <dl>
          <div v-for="w in WHO" :key="w.key" class="who-row" :data-you="w.key === reader || undefined">
            <dt>{{ w.label }}<span v-if="w.key === reader" class="you">You</span></dt>
            <dd>{{ whoSays(help, w.key) || "—" }}</dd>
          </div>
        </dl>
        <template v-if="guides.length">
          <h3 class="in-guide">In the guide</h3>
          <ul class="guides">
            <li v-for="g in guides" :key="g.id"><RouterLink :to="{ name: 'guide-topic', params: { topic: g.id } }">{{ g.title }}</RouterLink></li>
          </ul>
        </template>
        <button type="button" class="btn btn--sm close" @click="close(true)">Close</button>
      </section>
    </Teleport>
  </span>
</template>

<style scoped>
.screen-help { display: inline-flex; flex: none; }
.help-btn { width: 28px; height: 28px; color: var(--muted); }
.help-btn[aria-expanded="true"] { color: var(--ink); background: var(--bg-hover); }
.help-card {
  position: fixed; z-index: 70; width: 380px; max-width: calc(100vw - 24px); max-height: calc(100vh - 96px); overflow-y: auto;
  padding: var(--space-4) var(--space-5); background: var(--bg-elev); border: 1px solid var(--line);
  border-radius: var(--radius-m); box-shadow: var(--shadow-3); color: var(--ink); outline: none;
}
.help-card:focus-visible { box-shadow: var(--shadow-3), 0 0 0 2px color-mix(in srgb, var(--brand) 50%, transparent); }
.eyebrow { margin: 0; font-size: var(--text-xs); color: var(--muted); }
h2 { margin: var(--space-1) 0 var(--space-2); font-size: var(--text-md); }
.purpose { margin: 0 0 var(--space-4); font-size: var(--text-sm); line-height: 1.55; color: var(--ink-2); }
h3 { margin: 0 0 var(--space-2); font-size: var(--text-xs); font-weight: 600; color: var(--muted); }
dl { margin: 0; display: grid; gap: var(--space-2); }
.who-row { display: block; padding: var(--space-2) var(--space-3); border-radius: var(--radius-s); border: 1px solid transparent; }
.who-row[data-you] { border-color: color-mix(in srgb, var(--brand) 40%, var(--line)); background: var(--brand-soft); }
dt { display: flex; align-items: center; gap: var(--space-2); font-size: var(--text-xs); font-weight: 600; }
.you { font-size: 11px; font-weight: 600; padding: 0 var(--space-2); border-radius: 999px; background: var(--brand); color: var(--brand-ink); }
dd { margin: 2px 0 0; font-size: var(--text-sm); line-height: 1.5; color: var(--ink-2); }
.close { margin-top: var(--space-4); }
.in-guide { margin-top: var(--space-4); }
.guides { margin: 0; padding-left: var(--space-5); font-size: var(--text-sm); line-height: 1.6; }
@media (max-width: 720px) {
  .help-btn { width: 40px; height: 40px; }
  .help-card { left: 12px !important; right: 12px; width: auto; }
}
</style>
